import React, { useEffect, memo } from 'react';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import StopGalleryCard from './StopGalleryCard';
import ButtonHoverRender from '../Buttons/ButtonHover/ButtonHoverRender';
import ButtonHoverPrimary from '../Buttons/ButtonHover/ButtonHoverPrimary';
import * as constants from '../../constants';
import DOMPurify from 'dompurify';

import { getFullMedia } from 'selectors/mediaSelectors';

import './StopGallery.scss';

const StopGallery = memo((props) => {
  const {
    getIndex,
    getId,
    tourStops,
    activeStop,
    gaEvent,
    setYPosition,
    tourName,
    tourDescription,
    yPosition,
    t,
  } = props;

  const media = useSelector(getFullMedia);

  const getMediaById = (id, type) => {
    return media?.[type]?.[id] || {};
  };

  const isMobile = window.innerWidth < 1025;
  const isFocusable = yPosition === 0;
  const tabIndex = isFocusable ? 0 : -1;

  useEffect(() => {
    const setInnerHeight = () => {
      // fixes scrolling out of view on mobile
      // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
      const vh = window.innerHeight * 0.01;
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);

      if (yPosition !== 0) {
        setYPosition(window.innerHeight);
      }
    };

    window.addEventListener('resize', () => {
      setInnerHeight();
    });

    return () => window.removeEventListener('resize', setInnerHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeDrawer = () => {
    setYPosition(window.innerHeight);
  };

  const handleClick = (data, i) => {
    const { id, order, name, defaultPos } = data;
    if (typeof getId === 'function') {
      getId(id);
    }
    if (typeof getIndex === 'function') {
      getIndex(i);
    }
    // reports gaEvent
    if (typeof gaEvent === 'function') {
      gaEvent({
        defaultPos,
        stopNum: order || i,
        stopName: name,
        origin: 'Gallery',
      });
    }
    closeDrawer();
  };

  const handleEnter = (e, data, i) => {
    const { id, order, name, defaultPos } = data;

    if (typeof getIndex === 'function') {
      getIndex(i);
    }
    if (typeof getId === 'function') {
      getId(id);
    }
    // reports gaEvent
    if (typeof gaEvent === 'function') {
      gaEvent({
        defaultPos,
        stopNum: order || i,
        stopName: name,
        origin: 'Gallery',
      });
    }
    if (e.key === 'Enter') {
      closeDrawer();
    }
  };

  const renderStopGalleryCards = stops => {
    return stops.map((stop, i) => {
      if (!stop) return null;
      const {
        id,
        mainMediaType,
        name,
        order,
        panoId,
        thumbnail,
      } = stop;
      const isActive = activeStop === order;
      const mediaId = panoId || id;
      const media = getMediaById(mediaId, mainMediaType);
      return (
        <StopGalleryCard
          key={`${mediaId}-${i}`}
          mediaId={mediaId}
          handleClick={() => handleClick(stop, i)}
          handleEnter={(e) => handleEnter(e, stop, i)}
          mediaType={mainMediaType || constants.PANOS} // default to panos for old stop images that dont have the mainMediaType key/value
          thumbnail={thumbnail}
          isActive={isActive}
          name={name}
          alt={media?.alt ?? ''}
        />
      );
    });
  };

  return (
    <div
      className="stop-gallery"
      data-cy="stop-gallery"
      role="dialog"
      aria-labelledby="stop-gallery_heading"
      aria-describedby="stop-gallery_desc"
    >
      {!isMobile && (
        <ButtonHoverRender
          render={({ onEnter, onLeave, hovering }) => (
            <ButtonHoverPrimary
              tabIndex={tabIndex}
              onEnter={onEnter}
              onLeave={onLeave}
              hovering={hovering}
              className="stop-gallery__close-btn"
              handleClick={() => setYPosition()}
              aria-label={t('publicTour.ada.stopGallery.close')}
              isMobile={isMobile}
              data-cy="stop-gallery__close-btn"
            >
              <i aria-hidden="true" className="material-icons prev">
                close
              </i>
              &nbsp; {t('publicTour.stopGallery.close')}
            </ButtonHoverPrimary>
          )}
        />
      )}
      <div className="stop-gallery__info">
        <h2
          data-cy="stop-gallery-heading-2"
          className="stop-gallery__heading-2"
          id="stop-gallery_heading"
        >
          {`${tourName} ${t('publicTour.stopGallery.title')}`}
        </h2>
        <div
          data-cy="stop-gallery-description"
          className="stop-gallery__description"
          id="stop-gallery__desc"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tourDescription) }}
        />
      </div>
      <span className="stop-gallery__line" />
      <div className="stop-gallery__grid-wrapper">
        <section
          aria-label={t('publicTour.ada.stopGallery.tourStopsLabel')}
          className="stop-gallery__grid-wrapper__thumbnail-grid"
          data-cy="stop-gallery-list"
        >
          {renderStopGalleryCards(tourStops)}
        </section>
      </div>
    </div>
  );
});

export default withTranslation()(StopGallery);
